import { useRef, useState } from 'react'
import numberOrdinalSuffix from 'utils/numberOrdinalSuffix'
import { toast } from 'react-toastify';
import usePayrollEssentials from 'hooks/usePayrollEssentials';
import { createPaySchedule } from 'store/entities/payroll/payrollSetup/action';
import { ButtonLoader } from 'components/__new/common/form';
import useLoginInfo from 'hooks/useLoginInfo';
import logger from 'utils/logger';

const CreatePaySchedule = () => {
    const {useSelector, dispatch, history, SubPageTooltip, lettersRegex, removeNullProperties } = usePayrollEssentials()
    const submitRef = useRef(null)
    const [form, setForm] = useState({isProrated: true, frequency: "monthly", day_of_week: ""});
    const [frequency, setFrequency] = useState({});
    const { createPayScheduleLoading: createLoading } = useSelector(state => state.entities.payrollSetup)
    const {userInfo} = useLoginInfo();

    const isMonthlyFreq = form?.frequency === 'monthly'
    const isBiMonthlyFreq = form?.frequency === 'bi_monthly'
    const isWeeklyFreq = form?.frequency === 'weekly'

    const disablePastDate = () => {
        const today = new Date();
        const dd = String(today.getDate() + 1).padStart(2, "0");
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const yyyy = today.getFullYear();
        return yyyy + "-" + mm + "-" + dd;
    };

    const handleSubmit = (e) => {
        e.preventDefault()
        const first_run_percent = isBiMonthlyFreq ? Number(frequency?.first_run_percent) : null;
        const second_run_percent = isBiMonthlyFreq ? Number(frequency?.second_run_percent) : null;
        const first_run_day = isBiMonthlyFreq ? Number(frequency?.first_run_day) : null;
        const second_run_day = isBiMonthlyFreq ? Number(frequency?.second_run_day) : null;

        if(!form?.name?.length) {
            toast.error('Please enter a Pay Schedule name')
        } else if(isWeeklyFreq && !form?.day_of_week) {
            toast.error('Please select a day of the week')
        } else if(isBiMonthlyFreq && (first_run_percent + second_run_percent !== 100)) {
            toast.error('The percentage sum of the First and Second run percents for a bi-monthly pay schedule must be 100%')
        } else if(isBiMonthlyFreq && (first_run_day > second_run_day)) {
            toast.error('Second run day must be later than first run day')
        } else if(isBiMonthlyFreq && (first_run_day === second_run_day)) {
            toast.error('First and second run days cannot be the same')
        } else if(!lettersRegex.test(form?.name)) {
            toast.error('Pay schedule name must contain letters')
        } else {
            const payload = {
                name: form.name,
                frequency: {
                    payroll_frequency: "MONTHLY",
                    day_of_month: isMonthlyFreq ? Number(form?.day_of_month) : null,
                    day_of_week: isWeeklyFreq ? form?.day_of_week : null,
                    start_of_pay_period: isWeeklyFreq ? frequency?.start_of_pay_period : null,
                    first_run_day: isBiMonthlyFreq ? Number(frequency.first_run_day) : null,
                    first_run_percent: isBiMonthlyFreq ? Number(frequency?.first_run_percent) : null,
                    second_run_day: isBiMonthlyFreq ? Number(frequency.second_run_day ): null,
                    second_run_percent: isBiMonthlyFreq ? Number(frequency?.second_run_percent) : null,
                },
                workday_proration: false,
            }
            logger("Employer-Payroll-setup-payschedule-success", userInfo);
            dispatch(createPaySchedule(removeNullProperties(payload))).then(() => history.push('/payroll-configuration/pay-schedule'))
        }
    }

    const handleChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        setForm({...form, [name]: value})
    }

    const updateFrequency = (e) => {
        const name = e.target.name
        const value = e.target.value
        setFrequency({...frequency, [name]: value})
    }

    const weekdays = [
        {title: 'Su', value: 1},
        {title: 'Mo', value: 2},
        {title: 'Tu', value: 3},
        {title: 'We', value: 4},
        {title: 'Th', value: 5},
        {title: 'Fr', value: 6},
        {title: 'Sat', value: 7},
    ]

    return (
    <div className="w-full">
        <div className="border-b flex-btw items-center flex-wrap lg:flex-nowrap pb-3 mb-5 lg:mb-8">
            <div className="w-full lg:w-2/4">
                <div className="font-medium prl-bold flex items-center">
                    Create Pay Schedule
                    <SubPageTooltip
                        text={<span>This payroll setup is required for <br /> both Net Payroll and Gross Payroll.</span>}
                    />
                </div>
                <p className="mb-1 mt-1.5 text-xs lt-gray">Configure your pay schedule here.</p>
            </div>
            
            <div className="payroll-setup-subpage-action">
                <button disabled={createLoading} onClick={() => history.push("/payroll-configuration/pay-schedule")} className="btn btn-md btn-white bg-white mr-4 lg:mr-6">Cancel</button>
                <ButtonLoader  onClick={() => submitRef?.current?.click()} loading={createLoading} type="button" className="btn btn-md btn-ep-primary">
                    {createLoading ? "Configuring" : "Create Schedule"}
                </ButtonLoader>
            </div>
        </div>
        <form onSubmit={handleSubmit} className="w-full">
            <div className="border-b w-full pt-7 lg:pt-0">
                <div className="structure-wrap">
                    <p className="structure-text">Pay schedule name</p>
                    <div className="structure-input">
                    <input minLength={3} maxLength={50} required className="form-input" type="text" name='name'
                    placeholder="Enter your pay schedule name" onChange={handleChange} />
                    </div>
                </div>

                <div className="structure-wrap">
                    <p className="structure-text">Pay schedule frequency</p>
                    <div className="structure-input bg-white p-5">
                        <div className="w-full mb-5">
                            <p className="text-sm mb-1">Payroll Frequency</p>
                            <select disabled name="frequency" onChange={handleChange} className="form-input">
                                <option disabled value="">Select Frequency</option>
                                <option value="monthly">Monthly</option>
                                <option disabled value="bi_monthly">Bi-monthly</option>
                                <option disabled value="weekly">Weekly</option>
                            </select>
                        </div>

                        {isWeeklyFreq &&
                        <>
                        <div className="w-full mb-5">
                            <p className="text-sm mb-1">Day of the week</p>
                            <div className="w-full rounded-md border flex items-center justify-between py-2 px-1">
                                {weekdays.map(({title, value}) => {
                                const isActive = form?.day_of_week === value
                                return (
                                    <p key={value} onClick={() => setForm({...form, day_of_week: value})}
                                    className={`cursor-pointer h-7 w-7 rounded-full text-xs mb-0 flex items-center justify-center ${isActive ? "bg-ep-primary-600 text-white" : "text-black"}`}>
                                        {title}
                                    </p>
                                )
                                })}
                            </div>
                        </div>

                        <div className="w-full mb-5">
                            <p className="text-sm mb-1">Start of pay period</p>
                            <input type="date" name="start_of_pay_period" placeholder="here" min={disablePastDate()} className="form-input"
                                onChange={updateFrequency} required={isWeeklyFreq}
                            />
                        </div>
                        </>}

                        {isBiMonthlyFreq &&
                        <>
                        <hr className="deduct-divider" />
                            <div className='w-full'>
                                <div className="mb-5">
                                    <div className='w-full relative'>
                                        <p className="text-xs mb-1 lt-gray">1st run Percentage</p>
                                        <input required={isBiMonthlyFreq} className="form-input rounded-sm" type="number"
                                        id='prl-input' onChange={updateFrequency} name='first_run_percent' />
                                        <div className='input-percent-wrap'>%</div>
                                    </div>
                                </div>


                                <div className="w-full mb-5">
                                    <p className="text-xs mb-1 lt-gray">1st run day</p>
                                    <select required={isBiMonthlyFreq}  name="first_run_day" onChange={updateFrequency} className="form-input">
                                        <option value="">Select a day to run</option>
                                        {[...Array(32).keys()].slice(1).map((ele, index) =>
                                        <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                        )}
                                    </select>
                                </div>
                            </div>


                        <hr className="deduct-divider" />
                            <div className='w-full'>
                                <div className="mb-5">
                                    <div className='w-full relative'>
                                        <p className="text-xs mb-1 lt-gray">2nd run Percentage</p>
                                        <input required={isBiMonthlyFreq} className="form-input rounded-sm" type="number"
                                        id='prl-input' onChange={updateFrequency} name='second_run_percent' />
                                        <div className='input-percent-wrap'>%</div>
                                    </div>
                                </div>


                                <div className="w-full mb-5">
                                    <p className="text-xs mb-1 lt-gray">2nd run day</p>
                                    <select required={isBiMonthlyFreq}  name="second_run_day" onChange={updateFrequency} className="form-input">
                                        <option value="">Select a day to run</option>
                                        {[...Array(32).keys()].slice(1).map((ele, index) =>
                                        <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                        )}
                                    </select>
                                </div>
                            </div>
                        </>}

                        {/* {isMonthlyFreq && */}
                        <div className="w-full mb-5">
                            <p className="text-sm mb-1">Date of the month</p>
                            <select required={isMonthlyFreq} name="day_of_month" onChange={handleChange} className="form-input">
                                <option value="">Select a day to run monthly</option>
                                {[...Array(32).keys()].slice(1).map((ele, index) =>
                                <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                )}
                            </select>
                        </div>
                        {/* } */}

                    </div>
                </div>
                <button ref={submitRef} style={{display: 'none'}} type="submit">Continue</button>
            </div>
        </form>
    </div>
    )
}

export default CreatePaySchedule